// Engineer PWA Messages tab (Session ACZ): the engineer's single conversation with the office.
// Read and reply; replies queue offline and send on reconnect via the existing sync loop. An
// opt-in toggle controls the SMS nudge. Mobile-first, offline-aware, dark PWA styling.
import { presentableServerMessage } from "../../lib/serverMessage";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../lib/auth";
import { hasOwnMessageThread, isOversightAccount } from "../../lib/pwaIdentity";
import { OversightNotice } from "./OversightNotice";
import { api } from "../../lib/api";
import type { MessageThread, MessageDto, MessageThreadSummary, MessageJobOption } from "../../lib/api";
import { useEngineerData } from "./EngineerDataContext";
import { enqueueMessage, getQueuedMessages, syncMessages, retryQueuedMessage, removeQueuedMessage } from "../../lib/offline/messagesStore";
import { isMessageEscalated, messageEscalationReason } from "../../lib/offline/messageSync";
import type { QueuedMessage } from "../../lib/offline/messageSync";
import { ConfirmButton } from "../../components/ConfirmButton";
import { isPushSupported, getExistingSubscription, subscribeToPush } from "../../lib/pushClient";

function whenLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) + " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

// One queued reply that could not be delivered, with the two ways out of it. Shared by the normal
// thread view and the oversight notice below, so a reply is never visible on one and stranded on
// the other (Session AGV).
function StrandedReply({ q, onRetry, onRemove }: { q: QueuedMessage; onRetry: () => void; onRemove: () => void }) {
  return (
    <div className="rounded-ele border border-ele-danger/40 bg-[rgba(255,90,90,0.08)] p-3">
      <div className="whitespace-pre-wrap break-words text-sm text-ele-strong">{q.body}</div>
      <p className="mt-1 text-xs text-ele-danger">
        The office could not receive this message: {messageEscalationReason(q.escalatedStatus, q.lastError)}
      </p>
      <div className="mt-2 flex items-center gap-4">
        <button
          type="button"
          onClick={onRetry}
          className="min-h-11 rounded-ele bg-ele-green px-4 font-menu text-xs font-semibold tracking-wide text-ele-bg"
        >
          Retry
        </button>
        <ConfirmButton onConfirm={onRemove} label="Remove" className="text-xs text-ele-body" />
      </div>
    </div>
  );
}

export function EngineerMessages() {
  const { user } = useAuth();
  const { online } = useEngineerData();
  // This screen is one thread: THIS engineer and the office. An oversight account is the office, so
  // it has no thread of its own -- the server keys a thread on an engineer's user id and answers 404
  // for anyone else, the SMS toggle is engineer-only and 403s, and a reply with no named recipient
  // is refused 400. Before Session AGV all three fired anyway, on a 15-second poll (see the effects
  // below), so the Director's own phone produced a permanent error screen. See lib/pwaIdentity.ts.
  //
  // This is the MESSAGE-THREAD test, not the engineer-row one, and the difference is load-bearing
  // since Session AGW gave an admin account an Engineer row: holding a row makes a timesheet and a
  // van real, but it cannot invent a counterparty for a thread. The office cannot message itself
  // (validateMessagePairing), so this screen stays an explanation for an oversight account.
  const ownRecords = hasOwnMessageThread(user);
  const myId = ownRecords ? (user?.id ?? null) : null;

  const [thread, setThread] = useState<MessageThread | null>(null);
  const [queued, setQueued] = useState<QueuedMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [optIn, setOptIn] = useState<boolean | null>(null);
  const [hasMobile, setHasMobile] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  const reloadQueued = useCallback(async () => {
    setQueued(await getQueuedMessages());
  }, []);

  // `background` for the 15-second poll below (Session ASB review, F4): a timer's requests never keep a session alive.
  const load = useCallback(
    async (markRead: boolean, background = false) => {
      await reloadQueued();
      if (!navigator.onLine) {
        // Offline: keep whatever server thread we already have; queued replies still render.
        setLoading(false);
        return;
      }
      if (!myId) {
        setLoading(false);
        return;
      }
      try {
        const t = background ? await api.messages.thread(myId, { background: true }) : await api.messages.thread(myId);
        setThread(t);
        setLoadError(false);
        if (markRead) {
          const read = background ? api.messages.markRead(myId, { background: true }) : api.messages.markRead(myId);
          await read.catch(() => undefined);
        }
      } catch {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    },
    [myId, reloadQueued],
  );

  // Initial load marks the thread read. Also fetch the SMS opt-in state, which is an engineer-only
  // route (requireRole("engineer")) and so is not asked for on an oversight account.
  useEffect(() => {
    void load(true);
    if (!ownRecords) return;
    api.messages
      .smsPreference()
      .then((p) => {
        setOptIn(p.optIn);
        setHasMobile(p.hasMobile);
      })
      .catch(() => setOptIn(null));
  }, [load, ownRecords]);

  // When connectivity returns, drain any queued replies then reload (mirrors the job loop).
  useEffect(() => {
    if (!online) return;
    (async () => {
      await syncMessages();
      await load(false);
    })().catch(() => undefined);
  }, [online, load]);

  // Light poll so a new office message appears without a manual refresh. Mark read on each poll so a
  // message that arrives while the engineer is on this screen is treated as read (keeps the tab
  // badge honest); markRead is a no-op when nothing new has arrived.
  useEffect(() => {
    if (!ownRecords) return;
    const t = setInterval(() => void load(true, true), 15000);
    return () => clearInterval(t);
  }, [load, ownRecords]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [thread?.messages.length, queued.length]);

  async function send() {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await enqueueMessage({ body: text }); // persisted first -- never lost
      setBody("");
      await reloadQueued();
      if (navigator.onLine) {
        await syncMessages();
        await load(false);
      }
    } finally {
      setSending(false);
    }
  }

  async function toggleOptIn(next: boolean) {
    setOptIn(next); // optimistic
    try {
      await api.messages.setSmsPreference(next);
    } catch {
      setOptIn(!next); // revert on failure
    }
  }

  // Re-arm an escalated reply and drain (the engineer's "try again").
  const retry = useCallback(
    async (id: string) => {
      await retryQueuedMessage(id);
      await reloadQueued();
      if (navigator.onLine) {
        await syncMessages();
        await reloadQueued();
      }
    },
    [reloadQueued],
  );

  // Discard a queued reply the engineer gives up on.
  const remove = useCallback(
    async (id: string) => {
      await removeQueuedMessage(id);
      await reloadQueued();
    },
    [reloadQueued],
  );

  const messages = thread?.messages ?? [];
  // Split the outbox: escalated replies get a prominent block with Retry / Remove; the rest render
  // as ordinary pending bubbles in the conversation.
  const escalated = queued.filter(isMessageEscalated);
  const pending = queued.filter((q) => !isMessageEscalated(q));

  // An oversight account has no engineer thread of its own here (Session AGV): a thread is one
  // engineer and the office, and the office cannot message itself. RESTORED IN FULL 28 Sep 2026
  // (Director decision, reversing Session AKF's August retirement, then sharpened again the same
  // day): the office's own view of every engineer's thread now renders RIGHT HERE, on whichever
  // screen the office account is signed in on -- phone or laptop -- rather than merely pointing at
  // a separate desktop-only page. See OversightThreads below, which mirrors office/MessagesPage.tsx
  // but reads and sends live from this same screen.
  if (!ownRecords) {
    if (isOversightAccount(user)) {
      return (
        <>
          <OversightThreads />
          {/* Anything this account queued BEFORE the office could message from here at all is still
              in the outbox, and the sync loop runs from EngineerDataContext and the job store, not
              from this screen -- so it keeps being retried whatever is on screen. This screen is the
              only place such a reply can be removed. It can never be delivered (it was never
              addressed to a real thread), so Remove is the real way out; Retry is kept so the
              controls match the engineer's own view. */}
          {queued.length > 0 && (
            <div className="mt-4 space-y-2">
              <h2 className="font-heading text-lg text-ele-strong">Replies waiting to send</h2>
              <p className="text-sm text-ele-muted">
                These were queued on this account before it could message engineers directly. Remove them below, or
                use the conversation above to send a fresh reply.
              </p>
              {queued.map((q) => (
                <StrandedReply key={q.id} q={q} onRetry={() => void retry(q.id)} onRemove={() => void remove(q.id)} />
              ))}
            </div>
          )}
        </>
      );
    }
    return (
      <OversightNotice
        title="Messages"
        explanation="This account has no engineer profile, so it has no message thread of its own."
      />
    );
  }

  return (
    <div className="flex min-h-[70dvh] flex-col">
      <h1 className="mb-1 font-heading text-2xl tracking-wide text-ele-strong">Messages</h1>
      <p className="mb-3 text-sm text-ele-body">Chat with the East London Elevators office.</p>

      {/* SMS opt-in */}
      {optIn !== null && (
        <label className="mb-3 flex items-start gap-2 rounded-ele border border-ele-purple-accent bg-ele-surface p-3 text-sm">
          <input type="checkbox" checked={optIn} onChange={(e) => void toggleOptIn(e.target.checked)} className="mt-0.5 h-4 w-4 accent-ele-green" />
          <span>
            <span className="text-ele-strong">Text me when I get a message</span>
            {!hasMobile && optIn && (
              <span className="mt-0.5 block text-xs text-amber-400">The office needs your mobile number on file to text you.</span>
            )}
          </span>
        </label>
      )}

      {/* Conversation */}
      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
        {loading ? (
          <p className="text-sm text-ele-body">Loading...</p>
        ) : messages.length === 0 && pending.length === 0 ? (
          <p className="py-8 text-center text-sm text-ele-body">
            {loadError && !online ? "You are offline. Messages load when you are back online." : "No messages yet. Send one below."}
          </p>
        ) : (
          <>
            {messages.map((msg) => (
              <Bubble key={msg.id} msg={msg} />
            ))}
            {pending.map((q) => (
              <PendingBubble key={q.id} item={q} online={online} />
            ))}
          </>
        )}
      </div>

      {/* Escalated replies: could not be delivered, stopped retrying, need the engineer to act. */}
      {escalated.length > 0 && (
        <div className="mt-2 space-y-2">
          {escalated.map((q) => (
            <StrandedReply key={q.id} q={q} onRetry={() => void retry(q.id)} onRemove={() => void remove(q.id)} />
          ))}
        </div>
      )}

      {/* Compose */}
      <div className="mt-2 flex items-end gap-2">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          maxLength={2000}
          placeholder="Type a message..."
          className="min-h-11 flex-1 resize-none rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={sending || body.trim().length === 0}
          className="min-h-11 flex-none rounded-ele bg-ele-green px-4 font-menu text-sm font-semibold tracking-wide text-ele-bg transition disabled:opacity-40"
        >
          Send
        </button>
      </div>
      {!online && <p className="mt-1 text-xs text-ele-body opacity-70">You are offline. Your message will send when you are back online.</p>}
    </div>
  );
}

function Bubble({ msg }: { msg: MessageDto }) {
  // For the engineer, a message TO the engineer (office -> me) is inbound (left); one the engineer
  // sent (to_office) is outbound (right).
  const outbound = msg.direction === "to_office";
  return (
    <div className={`flex ${outbound ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[82%] rounded-ele border px-3 py-2 ${outbound ? "border-ele-green/30 bg-[rgba(99,196,31,0.1)]" : "border-ele-purple-accent bg-ele-inset"}`}>
        {!outbound && msg.fromUserName && <div className="mb-0.5 font-menu text-[10px] uppercase tracking-wide" style={{ color: "var(--ele-text-muted)" }}>{msg.fromUserName}</div>}
        <div className="whitespace-pre-wrap break-words text-sm text-ele-strong">{msg.body}</div>
        {msg.jobNumber && <div className="mt-1 text-[11px] text-ele-blue">Job #{msg.jobNumber}</div>}
        <div className="mt-1 text-right text-[10px]" style={{ color: "var(--ele-text-faint)" }}>{whenLabel(msg.createdAt)}</div>
      </div>
    </div>
  );
}

function PendingBubble({ item, online }: { item: QueuedMessage; online: boolean }) {
  const state = item.lastError ? "Will retry" : online ? "Sending..." : "Queued";
  return (
    <div className="flex justify-end">
      <div className="max-w-[82%] rounded-ele border border-ele-green/20 bg-[rgba(99,196,31,0.05)] px-3 py-2 opacity-80">
        <div className="whitespace-pre-wrap break-words text-sm text-ele-strong">{item.body}</div>
        <div className="mt-1 text-right font-menu text-[10px] uppercase tracking-wide text-ele-blue">{state}</div>
      </div>
    </div>
  );
}

// ---- The oversight view: every engineer's thread, read and answered from right here --------------
// (28 Sep 2026, Director decision). This mirrors office/MessagesPage.tsx (list, then one open
// conversation) but is styled for this mobile-first PWA shell and lives on the SAME screen an office
// account already lands on -- phone or laptop, whichever the office is signed in on -- rather than
// pointing away to a desktop-only page. It sends live (api.messages.send), never through the engineer
// offline-outbox: that outbox exists for a device that may lose signal mid-shift, which is the
// engineer's own problem, not the office's.
function OversightThreads() {
  const [threads, setThreads] = useState<MessageThreadSummary[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [threadsError, setThreadsError] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const loadThreads = useCallback(async (background = false) => {
    try {
      const list = await api.messages.threads({ background });
      setThreads(list);
      setThreadsError(false);
    } catch {
      setThreadsError(true);
    } finally {
      setLoadingThreads(false);
    }
  }, []);

  useEffect(() => {
    void loadThreads();
  }, [loadThreads]);

  // Light poll so a new engineer message shows without a manual refresh, matching the office page.
  useEffect(() => {
    const t = setInterval(() => void loadThreads(true), 20000);
    return () => clearInterval(t);
  }, [loadThreads]);

  if (selectedId) {
    return <OversightThreadPanel engineerUserId={selectedId} onBack={() => setSelectedId(null)} onChanged={loadThreads} />;
  }

  return (
    <div className="flex min-h-[70dvh] flex-col">
      <div className="mb-1 flex items-start justify-between gap-2">
        <h1 className="font-heading text-2xl tracking-wide text-ele-strong">Messages</h1>
        <OversightPushToggle />
      </div>
      <p className="mb-3 text-sm text-ele-body">Every engineer's conversation with the office, in one place.</p>
      {loadingThreads ? (
        <p className="text-sm text-ele-body">Loading...</p>
      ) : threadsError ? (
        <p className="text-sm text-ele-danger">Could not load messages.</p>
      ) : threads.length === 0 ? (
        <p className="py-8 text-center text-sm text-ele-body">No engineers to message yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {threads.map((t) => (
            <li key={t.engineerUserId}>
              <button
                type="button"
                onClick={() => setSelectedId(t.engineerUserId)}
                className="min-h-11 w-full rounded-ele border border-ele-field-border bg-ele-surface px-3 py-2.5 text-left transition hover:border-ele-green/50"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-heading text-sm text-ele-strong">{t.displayName}</span>
                  {t.unreadCount > 0 && (
                    <span className="ele-pill ele-pill--green flex-none" title={`${t.unreadCount} unread`}>
                      {t.unreadCount}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex items-center justify-between gap-2">
                  <span className="truncate text-xs text-ele-body">
                    {t.lastMessagePreview ? (
                      <>
                        {t.lastMessageDirection === "to_engineer" && <span className="text-ele-body/60">You: </span>}
                        {t.lastMessagePreview}
                      </>
                    ) : (
                      <span className="italic opacity-60">No messages yet</span>
                    )}
                  </span>
                  <span className="flex-none text-[10px] uppercase tracking-wide text-ele-body/60">{t.lastMessageAt ? whenLabel(t.lastMessageAt) : ""}</span>
                </div>
                {t.techId && <div className="mt-0.5 font-menu text-[10px] uppercase tracking-wide text-ele-body/50">{t.techId}</div>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// A quiet affordance to turn on push notifications for THIS device, right where an oversight account
// is missing them: the desktop office page has always had one (OfficePushToggle, MessagesPage.tsx),
// but that page went unrouted with Session AKF, so nothing ever asked this account's browser for
// permission -- which is the whole reason an engineer's message stopped reaching the office at all
// (28 Sep 2026, Director-reported: no notification on the laptop). Shown only when push is supported,
// VAPID is configured, and this browser is not already subscribed.
function OversightPushToggle() {
  const [supported] = useState(() => isPushSupported());
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) return;
      let status;
      try {
        status = await api.push.status();
      } catch {
        return;
      }
      if (cancelled || !status.configured || !status.publicKey) return;
      setPublicKey(status.publicKey);
      const existing = await getExistingSubscription();
      if (cancelled) return;
      setSubscribed(Boolean(existing));
    })().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = useCallback(async () => {
    if (busy || !publicKey) return;
    setBusy(true);
    const result = await subscribeToPush(publicKey);
    setBusy(false);
    if (result.ok) setSubscribed(true);
  }, [busy, publicKey]);

  if (!supported || !publicKey || subscribed === null) return null;
  if (subscribed) return <span className="text-[11px] text-ele-body">Notifications on</span>;
  return (
    <button
      type="button"
      onClick={() => void enable()}
      disabled={busy}
      className="min-h-11 rounded-ele border border-ele-green px-2 text-[11px] text-ele-green disabled:opacity-40"
    >
      {busy ? "Enabling..." : "Enable notifications"}
    </button>
  );
}

function OversightThreadPanel({
  engineerUserId,
  onBack,
  onChanged,
}: {
  engineerUserId: string;
  onBack: () => void;
  onChanged: (background?: boolean) => void;
}) {
  const [thread, setThread] = useState<MessageThread | null>(null);
  const [jobs, setJobs] = useState<MessageJobOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [body, setBody] = useState("");
  const [jobId, setJobId] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const load = useCallback(
    async (markRead: boolean, background = false) => {
      try {
        const t = await api.messages.thread(engineerUserId, { background });
        setThread(t);
        setLoadError(false);
        if (markRead) {
          await api.messages.markRead(engineerUserId, { background }).catch(() => undefined);
          onChanged(background);
        }
      } catch {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    },
    [engineerUserId, onChanged],
  );

  // Initial load marks the thread read; the poll below does not (mirrors office/MessagesPage.tsx).
  useEffect(() => {
    void load(true);
    api.messages
      .threadJobs(engineerUserId)
      .then(setJobs)
      .catch(() => setJobs([]));
  }, [engineerUserId, load]);

  useEffect(() => {
    const t = setInterval(() => void load(true, true), 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [thread?.messages.length]);

  async function send() {
    const text = body.trim();
    if (!text || sending) return;
    const to = thread?.sendToUserId;
    if (!to) {
      setSendError("No recipient available for this thread.");
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      await api.messages.send({ toUserId: to, body: text, jobId: jobId || undefined });
      setBody("");
      setJobId("");
      await load(true);
    } catch (e) {
      setSendError((e instanceof Error && presentableServerMessage(e.message)) || "Could not send the message. Check your signal and try again: what you typed is still here.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex min-h-[70dvh] flex-col">
      <div className="mb-2 flex flex-none items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="min-h-11 flex-none rounded-ele border border-ele-field-border px-3 font-menu text-xs text-ele-green"
        >
          ‹ Back
        </button>
        <div className="min-w-0">
          <div className="truncate font-heading text-lg text-ele-strong">{thread?.engineer.displayName ?? "Conversation"}</div>
          {thread?.engineer.techId && <div className="font-menu text-[10px] uppercase tracking-wide text-ele-body/60">{thread.engineer.techId}</div>}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
        {loading ? (
          <p className="text-sm text-ele-body">Loading...</p>
        ) : loadError ? (
          <p className="text-sm text-ele-danger">Could not load this conversation.</p>
        ) : thread && thread.messages.length === 0 ? (
          <p className="py-8 text-center text-sm text-ele-body">No messages yet. Say hello below.</p>
        ) : (
          thread?.messages.map((msg) => <OversightBubble key={msg.id} msg={msg} />)
        )}
      </div>

      <div className="mt-2">
        {jobs.length > 0 && (
          <select
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
            aria-label="Link a job (optional)"
            className="mb-2 min-h-11 w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
          >
            <option value="">Link a job (optional)</option>
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>
                {j.label}
              </option>
            ))}
          </select>
        )}
        <div className="flex items-end gap-2">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder="Type a message..."
            className="min-h-11 flex-1 resize-none rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
          />
          <button
            type="button"
            onClick={() => void send()}
            disabled={sending || body.trim().length === 0}
            className="min-h-11 flex-none rounded-ele bg-ele-green px-4 font-menu text-sm font-semibold tracking-wide text-ele-bg transition disabled:opacity-40"
          >
            {sending ? "Sending..." : "Send"}
          </button>
        </div>
        {sendError && <p className="mt-1.5 text-xs text-ele-danger">{sendError}</p>}
      </div>
    </div>
  );
}

function OversightBubble({ msg }: { msg: MessageDto }) {
  // For an oversight viewer (the office), a message the office sent (to_engineer) is outbound
  // (right); one the engineer sent (to_office) is inbound (left) -- the OPPOSITE mapping from the
  // engineer's own Bubble above, because `direction` is defined relative to the thread's engineer,
  // never the viewer (server/src/modules/messages/messages.ts).
  const outbound = msg.direction === "to_engineer";
  return (
    <div className={`flex ${outbound ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[82%] rounded-ele border px-3 py-2 ${outbound ? "border-ele-green/30 bg-[rgba(99,196,31,0.1)]" : "border-ele-purple-accent bg-ele-inset"}`}>
        {!outbound && msg.fromUserName && <div className="mb-0.5 font-menu text-[10px] uppercase tracking-wide" style={{ color: "var(--ele-text-muted)" }}>{msg.fromUserName}</div>}
        <div className="whitespace-pre-wrap break-words text-sm text-ele-strong">{msg.body}</div>
        {msg.jobNumber && <div className="mt-1 text-[11px] text-ele-blue">Job #{msg.jobNumber}</div>}
        <div className="mt-1 text-right text-[10px]" style={{ color: "var(--ele-text-faint)" }}>{whenLabel(msg.createdAt)}</div>
      </div>
    </div>
  );
}
